/**
 * Imágenes que el lector de documentos no admite → JPEG en el navegador (5-10-2026).
 *
 * Textract solo lee JPEG, PNG, TIFF y PDF. La herramienta aceptaba cualquier `image/*`: un visitante subió un `.webp` (una
 * descarga típica de internet) y el procesamiento falló sin que se le dijera nada claro. WebP, GIF, BMP y AVIF se decodifican en
 * cualquier navegador moderno, así que se convierten aquí; HEIC solo lo decodifica Safari: en el resto se rechaza con un mensaje.
 *
 *   toSupportedImage(file) → File (el mismo si ya sirve) | null (no se pudo convertir)
 */
const NATIVE = ["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/tiff"];
const MAX_SIDE = 4000;

export const needsConversion = (f) => {
  const t = String((f && f.type) || "").toLowerCase();
  return t.startsWith("image/") && !NATIVE.includes(t);
};

export async function toSupportedImage(file) {
  if (!needsConversion(file)) return file;
  try {
    if (typeof createImageBitmap !== "function" || typeof document === "undefined") return null;
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; // fondos transparentes (PNG/WebP/GIF) no se quedan negros en JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (typeof bitmap.close === "function") bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    if (!blob) return null;
    return new File([blob], String(file.name || "image").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: file.lastModified });
  } catch {
    return null;
  }
}
